import { html, query, property, PropertyValues } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
import { KatLitElement, register } from '../../shared/base';
import baseStyles from '../../shared/base/base.lit.scss';
import { checkSlots, getNodeSlot } from '../../shared/slot-utils';
import {
  createEventManager,
  EventManager,
  isTabNext,
  isTabPrevious,
} from '../../utils/event-utils';
import { KatPopover } from '../popover';
import { SmallBreakPoint } from '../../utils/breakpoints';
import styles from './navigation.lit.scss';
import getString from './strings';
import { KatNavigationSection } from './navigation-section';

const ELLIPSIS_WIDTH = 20;

/**
 * @component {kat-navigation} KatNavigation The in-product navigation component should be used to provide access to additional pages and workflows in a single product or task.
 * @status Production
 * @theme flo
 * @subcomponent ./navigation-section.ts
 * @slot default Place "a" or "kat-navigation-section" elements here. Mark the active link with a "data-active" attribute.
 * @slot label Place a "span" element here.  This represents the clicked item from the global nav, and needs to be manually updated as the selected navigation element changes.
 * @guideline Do Use either "a" or "kat-navigation-section" elements as children.
 * @guideline Do Mark the active link using a data-active attribute.
 * @guideline Do When your design calls for only a "left" nav, use mode "responsive" and place this in the "left" slot of your kat-app-layout component.
 * @guideline Do When your design calls for only a "top" nav, use mode "horizontal" and place this in the "top" slot of your kat-app-layout component.
 * @guideline Do When your design calls for both "top" and "left" nav, use mode "horizontal" for the top, and mode "vertical" for the left.
 * @guideline Don't use kat-navigation-section when mode is horizontal.
 * @guideline Don’t allow users to navigate away from the navigation.
 * @guideline Don’t duplicate the global navigation structure in your product navigation
 * @guideline Don’t use multiple nav menus in your product, unless used for pseudo-navigation, such as filtering in search results or the app store.
 * @guideline Don’t use this for sequential steps, or as a replacement for <a href="/components/tabs/">tabs</a>.
 * @example Horizontal { "mode": "horizontal", "content": "<span slot=\"label\">Label Slot</span><a href=\"#\">Home</a><a href=\"#\" data-active>About Us</a><a href=\"#\">Events</a><a href=\"#\">About Us</a><a href=\"#\">Media</a><a href=\"#\">About Us</a><a href=\"#\">Contact</a>" }
 * @example Vertical { "mode": "vertical", "content": "<span slot=\"label\">Label Slot</span><a href=\"#\" data-active>Home</a><a href=\"#\">About Us</a><kat-navigation-section><span slot=\"label\">Product Categories</span><a href=\"#\">Apparel</a><a href=\"#\">Home & Garden</a><a href=\"#\">Sporting Goods</a><a href=\"#\">Health & Wellness</a><a href=\"#\">Pets</a><a href=\"#\">Electronics</a></kat-navigation-section><a href=\"#\">Learn</a><a href=\"#\">Contact Us</a>" }
 * @example Responsive { "mode": "responsive", "content": "<a href=\"#\">Home</a><a href=\"#\" data-active>About Us</a><a href=\"#\">Events</a><a href=\"#\">About Us</a><a href=\"#\">Media</a><a href=\"#\">About Us</a><a href=\"#\">Contact</a>" }
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-navigation')
export class KatNavigation extends KatLitElement {
  static get styles() {
    return [baseStyles, styles];
  }

  /**
   * How the navigation element should render and respond to screen size.
   * Possible values are:
   *
   * * **responsive** (default)
   *
   *    The navigation element will flip from vertical to horizontal at the
   *    small breakpoint. This should be used when only a left nav is needed,
   *    and this element should be placed in `kat-app-layout`'s `left` slot.
   *
   * * **horizontal**
   *
   *    The navigation element will always display horizontally, regardless of
   *    the screen size. When either _only top nav_ is needed, or _both left and
   *    top nav_ are needed, the top nav element should have `mode` set to
   *    `horizontal` so that it doesn't try to adjust on larger screens. A
   *    `horizontal` nav element should be placed in `kat-app-layout`'s `top`
   *    slot.
   *
   * * **vertical**
   *
   *    The navigation element will always display vertically, regardless of the
   *    screen size. When using both a top and left nav, the left nav element
   *    should have `mode` set to `vertical` so that it doesn't try to adjust on
   *    smaller screens. A `vertical` nav element should be placed in
   *    `kat-app-layout`'s `left` slot.
   *
   * @enum {value} responsive Flip to horizontal nav at the small breakpoint.
   * @enum {value} horizontal Always display horizontally, regardless of screen size.
   * @enum {value} vertical Always display vertically, regardless of screen size.
   */
  @property()
  mode: 'responsive' | 'horizontal' | 'vertical' = 'responsive';

  // this property is controlled internally, but we need it to reflect to an
  // attribute, which is why we aren't using `internalProperty` here.
  @property()
  private hz = false;

  @query('.right')
  private _right: HTMLDivElement;

  @query('kat-popover:not(.hide)')
  private _popover: KatPopover;

  private _handleOverflowManager: EventManager;

  constructor() {
    super();

    this._handleOverflow = this._handleOverflow.bind(this);
    this._handleOverflowManager = createEventManager([
      ['resize', this._handleOverflow],
    ]);
    this._listeners = [
      {
        target: window,
        listeners: [['resize', () => this.handleWindowResize()]],
      },
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    this._toggleOverflow();

    // temporary fix to allow testing to pass: this is only an issue with Safari
    // and i was unable to reproduce manually or in standard integration tests: https://sim.amazon.com/issues/KDS-3432
    if (typeof SmallBreakPoint.addEventListener === 'function') {
      SmallBreakPoint.addEventListener('change', this._toggleOverflow);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._handleOverflowManager.off(window);
    SmallBreakPoint.removeEventListener('change', this._toggleOverflow);
  }

  firstUpdated() {
    this.updateBlurState();
  }

  private handleWindowResize() {
    this.updateBlurState();
  }

  updated(changed: PropertyValues<this>) {
    if (changed.has('mode')) {
      this._toggleOverflow();

      // We call this again with a delay, otherwise _all_ children end up within
      // the overflow for some reason.
      requestAnimationFrame(this._handleOverflow);
    }
  }

  private _toggleOverflow = () => {
    // Enable the resize observer only when `horizontal` mode is set and we
    // are not at the small break point
    this._handleOverflowManager.set(
      window,
      this.mode === 'horizontal' && !SmallBreakPoint.matches
    );

    // We add this attribute whenever the component is displayed horizontally
    // because it greatly simplifies our css. Otherwise we end up with a lot
    // of duplication between `mode="horizontal"` and the media query.
    this.hz =
      this.mode === 'horizontal' ||
      (this.mode === 'responsive' && SmallBreakPoint.matches);

    this._updateChildren();

    // additionally, make sure that we always trigger this once
    this._handleOverflow();
  };

  private _getNavSections(openOnly = false) {
    const openSelector = openOnly ? '[open]' : '';
    return this.querySelectorAll<KatNavigationSection>(
      `kat-navigation-section[hz]${openSelector}:not([slot="overflow"])`
    );
  }

  private _closeNavSections() {
    this._getNavSections(true).forEach(section => (section.open = false));
  }

  private _positionNavSections() {
    this._getNavSections().forEach(section => {
      section.shadowRoot.querySelector<HTMLElement>('#content').style.left =
        section.getBoundingClientRect().left + 'px';
    });
  }

  private _updateChildren() {
    Array.from(this.children).forEach(child => {
      if (child.tagName === 'KAT-NAVIGATION-SECTION') {
        (child as any).hz = this.hz;
      }
    });
  }

  private _handleOverflow() {
    const right = this._right;
    if (!right) {
      // This function can get called before we've ever rendered, in which case
      // there won't be a right element yet. We bail in this case. There's
      // probably a better way to do this, but this way didn't seem all that
      // bad.
      return;
    }

    this._closeNavSections();

    const availableWidth = right.clientWidth;
    let usedWidth = 0;
    let needsUpdate;

    // For each element in the default slot, get its width + margin and make
    // sure it won't overflow our container. If it will, then we pop an
    // `overflow` slot onto it to hide it inside our popover.
    Array.from(this.children).some(child => {
      if (!['default', 'overflow'].includes(getNodeSlot(child))) {
        return;
      }

      const prevSlot = child.slot;

      // We reset the slot so that we can get its `clientWidth` value. If we
      // don't do this, then an overflown element will report a `0` width
      // because it will be within a `display: none` container.
      child.slot = '';

      const setOverflow = on => {
        const newSlot = on ? 'overflow' : '';
        child.slot = newSlot;
        if (newSlot !== prevSlot) {
          needsUpdate = true;
        } else {
          // A small optimization - if the slot _has not_ changed, and it is set
          // to overflow, then we know the remaining children will also already
          // be set to overflow and will remain that way. Returning `true` here
          // will break out of the loop.
          return on;
        }
      };

      if (SmallBreakPoint.matches || this.mode !== 'horizontal') {
        // Remove the overflow here - we only overflow when the screen is large
        // && the mode is horizontal
        return setOverflow(false);
      }

      usedWidth +=
        child.clientWidth + parseInt(getComputedStyle(child).marginRight);

      const willFit = usedWidth < availableWidth;

      if (willFit) {
        const hasSibling = !!child.nextElementSibling;
        if (hasSibling && availableWidth - usedWidth < ELLIPSIS_WIDTH) {
          // If we have a sibling but there isn't enough room left for the
          // ellipsis, then we have to overflow this child.
          return setOverflow(true);
        }
        return setOverflow(false);
      }

      return setOverflow(true);
    });

    if (needsUpdate) {
      // If any overflow slots have changed, then we need to request an update
      // so that we can show/hide the popover.
      this.requestUpdate();
    }
  }

  private async _handleKeydown(e: KeyboardEvent) {
    if (isTabNext(e)) {
      // We want to automatically open the popover and jump to the first anchor
      // inside if the popover is in use, otherwise the useability is quite awkward.

      const nextElement = (e.target as HTMLElement)
        .nextElementSibling as HTMLElement;
      if (!nextElement && this._popover) {
        if (this._popover.visible) {
          // TODO: This seems like a bug in popover. It should close when it or
          // a child loses focus, but those listeners are only set up if the
          // popover-trigger is involved when the popover is shown. When calling
          // popover.show like we do below, those listeners are not set up, thus
          // we see the behavior where the popover does not close automatically.
          // We must close it manually.
          this._popover.hide();
        }
        return;
      }

      if (nextElement?.slot === 'overflow') {
        e.preventDefault();
        this._popover.show();
        // We need to show the popover first before we can focus the element.
        // If we don't await the update, then the first overflown element won't
        // actually be focusable because the popover is still hidden.
        await this._popover.updateComplete;
        nextElement.focus();
      }

      // Otherwise let the default tab behavior happen
      return;
    }

    if (isTabPrevious(e)) {
      // We only special case previous tab behavior when transitioning between
      // an overflown and a visible element.
      if ((e.target as HTMLElement).slot !== 'overflow') {
        return;
      }

      const previousElement = (e.target as HTMLElement)
        .previousElementSibling as HTMLElement;

      if (previousElement?.slot !== 'overflow') {
        // Make sure we tab to the previous anchor tag, _not_ to the popover element.
        e.preventDefault();
        previousElement.focus();
        if (this._popover.visible) {
          this._popover.hide();
        }
      }
    }
  }

  private _handleFocus(e: FocusEvent) {
    // When tabbing backwards into the nav, and the popover is in use, the first
    // tabbable element is the button trigger. To improve the experience, we
    // then manually focus the last nav item.
    if ((e.target as HTMLElement).slot === 'trigger') {
      (this.lastElementChild as HTMLElement).focus();
    }
  }

  private _handleScroll() {
    this._closeNavSections();
    this.updateBlurState();
  }

  private _handleSectionToggle() {
    this._positionNavSections();
  }

  private updateBlurState() {
    const navBar = this.shadowRoot.querySelector('.nav-bar');
    if (!navBar) return;

    const isNotScrolled = navBar.scrollLeft <= 0;
    const isMaxScrolled =
      navBar.scrollWidth - navBar.scrollLeft <= navBar.clientWidth + 1;

    this.classList.toggle('show-left-nav-blur', !isNotScrolled);
    this.classList.toggle('show-right-nav-blur', !isMaxScrolled);
  }

  private renderLeftMobileBlur() {
    if (this.mode === 'vertical') return;

    return html`<div
      class="blur-left"
      part="navigation-mobile-left-blur"
      aria-label="left nav blur"
    ></div>`;
  }

  private renderRightMobileBlur() {
    if (this.mode === 'vertical') return;

    return html`<div
      class="blur-right"
      part="navigation-mobile-right-blur"
      aria-label="right nav blur"
    ></div>`;
  }

  render() {
    const slots = checkSlots(this);
    const popoverClasses = { hide: !slots.overflow };
    const labelClasses = { hide: !slots.label };

    return html`<nav class="nav-bar" @scroll=${this._handleScroll}>
      <div class="left ${classMap(labelClasses)}">
        <slot name="label"></slot
        ><kat-icon class="arrow" size="tiny" name="arrow-right"></kat-icon>
      </div>
      <div
        class="right"
        @keydown=${this._handleKeydown}
        @focusin=${this._handleFocus}
        @toggle=${this._handleSectionToggle}
      >
        ${this.renderLeftMobileBlur()}
        <slot @slotchange=${this._updateChildren}></slot
        ><kat-popover
          trigger-type="hover"
          kat-aria-behavior="interactive"
          class="popover ${classMap(popoverClasses)}"
          ><button
            slot="trigger"
            aria-label="${getString('kat-navigation-overflow-expand')}"
          >
            <kat-icon
              name="more_horiz"
              size="tiny"
              data-popover-anchor
            ></kat-icon>
          </button>
          <div class="overflow">
            <slot
              name="overflow"
              @slotchange=${this._updateChildren}
            ></slot></div
        ></kat-popover>
        ${this.renderRightMobileBlur()}
      </div>
    </nav>`;
  }
}
